import { PLAYER_SHIP_CLASSES, PlayerShipClass, SHIP_UNLOCK_LEVEL } from '@entities/PlayerShips';
import { getPlayerShipMaxHull } from '@entities/EntityFactory';
import { shipName } from '@/fleet/ShipNames';

/** Docking berths around the mothership; the roster can never be larger. */
export const BERTH_COUNT = 10;

export const SHIP_COST: Record<PlayerShipClass, number> = {
  fighter: 250, dart: 300, gunship: 450, longbow: 400, warden: 550,
};

export const REPAIR_COST_PER_HULL = 40;

/** Credits paid out for a mission. A completed mission adds a bonus and a larger share of the score. */
export function creditsForMission(score: number, completed: boolean): number {
  const safeScore = Math.max(0, score);
  return completed ? 150 + Math.round(safeScore * 0.5) : Math.round(safeScore * 0.2);
}

export interface RosterShip {
  id: number;
  name: string;
  shipClass: PlayerShipClass;
  hull: number;
  maxHull: number;
  missions: number;
  berth: number;
}

export interface Berth {
  berth: number;
  ship?: RosterShip;
  /** Name of the ship destroyed from this berth, until a replacement is built */
  lostName?: string;
}

/** What happened to the roster ships that flew a mission. */
export interface MissionReport {
  /** Roster ids of every ship that entered combat, reinforcements included */
  participants: number[];
  survivors: { id: number; hull: number }[];
  score: number;
  completed: boolean;
}

export interface MissionOutcome {
  creditsEarned: number;
  lost: RosterShip[];
  survivors: RosterShip[];
}

export type BuildRefusal = { ok: false; reason: 'locked' | 'credits' | 'hangarFull' };
export type BuildResult = { ok: true; ship: RosterShip; cost: number } | BuildRefusal;

export type DeployResult = 'deployed' | 'recalled' | 'full' | 'missing';

export interface FleetData {
  ships: RosterShip[];
  credits: number;
  deployed: number[];
  lostBerths: { berth: number; name: string }[];
  commissioned: number;
}

/** The player's persistent roster of named ships, their credits and the next deployment. */
export class Fleet {
  private ships: RosterShip[] = [];
  private deployed: number[] = [];
  private lostBerths = new Map<number, string>();
  private commissioned = 0;

  private constructor(private credits: number) {}

  /** A fresh roster of full-hull ships, all deployed. */
  static fromClasses(classes: PlayerShipClass[], credits: number = 0): Fleet {
    const fleet = new Fleet(credits);
    for (const shipClass of classes.slice(0, BERTH_COUNT)) {
      const ship = fleet.commission(shipClass, fleet.freeBerth()!);
      fleet.deployed.push(ship.id);
    }
    return fleet;
  }

  static fromData(data: unknown): Fleet | null {
    if (!data || typeof data !== 'object') return null;
    const raw = data as Partial<FleetData>;
    if (!Array.isArray(raw.ships) || typeof raw.credits !== 'number') return null;

    const ships: RosterShip[] = [];
    for (const s of raw.ships as Partial<RosterShip>[]) {
      if (!s || !PLAYER_SHIP_CLASSES.includes(s.shipClass as PlayerShipClass)) return null;
      if (typeof s.id !== 'number' || typeof s.name !== 'string' || typeof s.berth !== 'number') return null;
      if (s.berth < 0 || s.berth >= BERTH_COUNT || ships.some(other => other.berth === s.berth || other.id === s.id)) return null;
      const maxHull = getPlayerShipMaxHull(s.shipClass!);
      ships.push({
        id: s.id,
        name: s.name,
        shipClass: s.shipClass!,
        maxHull,
        hull: Math.max(1, Math.min(maxHull, typeof s.hull === 'number' ? s.hull : maxHull)),
        missions: typeof s.missions === 'number' ? s.missions : 0,
        berth: s.berth,
      });
    }

    const fleet = new Fleet(Math.max(0, raw.credits));
    fleet.ships = ships;
    const ids = new Set(ships.map(s => s.id));
    fleet.deployed = Array.isArray(raw.deployed) ? raw.deployed.filter(id => ids.has(id)) : [];
    for (const lost of Array.isArray(raw.lostBerths) ? raw.lostBerths : []) {
      if (typeof lost?.berth === 'number' && typeof lost.name === 'string') fleet.lostBerths.set(lost.berth, lost.name);
    }
    const highestId = ships.reduce((max, s) => Math.max(max, s.id), 0);
    fleet.commissioned = Math.max(typeof raw.commissioned === 'number' ? raw.commissioned : 0, highestId);
    return fleet;
  }

  toData(): FleetData {
    return {
      ships: this.ships.map(s => ({ ...s })),
      credits: this.credits,
      deployed: [...this.deployed],
      lostBerths: [...this.lostBerths].map(([berth, name]) => ({ berth, name })),
      commissioned: this.commissioned,
    };
  }

  getShips(): RosterShip[] {
    return this.ships;
  }

  getShip(id: number): RosterShip | undefined {
    return this.ships.find(s => s.id === id);
  }

  getCredits(): number {
    return this.credits;
  }

  getDeployed(): RosterShip[] {
    return this.deployed.map(id => this.getShip(id)!).filter(Boolean);
  }

  isDeployed(id: number): boolean {
    return this.deployed.includes(id);
  }

  /** Roster ships staying aboard; reinforcements are drawn from these in roster order. */
  getReserve(): RosterShip[] {
    return this.ships.filter(s => !this.deployed.includes(s.id));
  }

  getBerths(): Berth[] {
    return Array.from({ length: BERTH_COUNT }, (_, berth) => {
      const ship = this.ships.find(s => s.berth === berth);
      if (ship) return { berth, ship };
      const lostName = this.lostBerths.get(berth);
      return lostName ? { berth, lostName } : { berth };
    });
  }

  /** Price of the next ship of a class. A stranded player gets a fighter for free. */
  buildCost(shipClass: PlayerShipClass): number {
    const stranded = this.ships.length === 0 && this.credits < Math.min(...Object.values(SHIP_COST));
    return stranded && shipClass === 'fighter' ? 0 : SHIP_COST[shipClass];
  }

  canBuild(shipClass: PlayerShipClass, level: number): { ok: true } | BuildRefusal {
    if (SHIP_UNLOCK_LEVEL[shipClass] > level) return { ok: false, reason: 'locked' };
    if (this.freeBerth() === undefined) return { ok: false, reason: 'hangarFull' };
    if (this.credits < this.buildCost(shipClass)) return { ok: false, reason: 'credits' };
    return { ok: true };
  }

  build(shipClass: PlayerShipClass, level: number): BuildResult {
    const check = this.canBuild(shipClass, level);
    if (!check.ok) return check;

    const cost = this.buildCost(shipClass);
    this.credits -= cost;
    const ship = this.commission(shipClass, this.freeBerth()!);
    return { ok: true, ship, cost };
  }

  repairCost(id: number): number {
    const ship = this.getShip(id);
    return ship ? (ship.maxHull - ship.hull) * REPAIR_COST_PER_HULL : 0;
  }

  repair(id: number): boolean {
    const ship = this.getShip(id);
    const cost = this.repairCost(id);
    if (!ship || cost === 0 || cost > this.credits) return false;

    this.credits -= cost;
    ship.hull = ship.maxHull;
    return true;
  }

  toggleDeploy(id: number, limit: number): DeployResult {
    if (!this.getShip(id)) return 'missing';
    if (this.deployed.includes(id)) {
      this.deployed = this.deployed.filter(d => d !== id);
      return 'recalled';
    }
    if (this.deployed.length >= limit) return 'full';
    this.deployed.push(id);
    return 'deployed';
  }

  trimDeployment(limit: number): void {
    this.deployed = this.deployed.slice(0, Math.max(0, limit));
  }

  applyMission(report: MissionReport): MissionOutcome {
    const survivorHull = new Map(report.survivors.map(s => [s.id, s.hull]));
    const lost: RosterShip[] = [];
    const survivors: RosterShip[] = [];

    for (const id of new Set(report.participants)) {
      const ship = this.getShip(id);
      if (!ship) continue;

      const hull = survivorHull.get(id);
      if (hull === undefined || hull <= 0) {
        lost.push(ship);
        continue;
      }
      ship.hull = Math.min(ship.maxHull, Math.ceil(hull));
      ship.missions++;
      survivors.push(ship);
    }

    for (const ship of lost) {
      this.ships = this.ships.filter(s => s.id !== ship.id);
      this.deployed = this.deployed.filter(id => id !== ship.id);
      this.lostBerths.set(ship.berth, ship.name);
    }

    const creditsEarned = creditsForMission(report.score, report.completed);
    this.credits += creditsEarned;
    return { creditsEarned, lost, survivors };
  }

  private commission(shipClass: PlayerShipClass, berth: number): RosterShip {
    this.commissioned++;
    const maxHull = getPlayerShipMaxHull(shipClass);
    const ship: RosterShip = {
      id: this.commissioned,
      name: shipName(this.commissioned),
      shipClass,
      hull: maxHull,
      maxHull,
      missions: 0,
      berth,
    };
    this.ships.push(ship);
    this.lostBerths.delete(berth);
    return ship;
  }

  /** Replacements fill the berths of lost ships first, then the lowest empty berth. */
  private freeBerth(): number | undefined {
    const taken = new Set(this.ships.map(s => s.berth));
    const free = Array.from({ length: BERTH_COUNT }, (_, i) => i).filter(i => !taken.has(i));
    return free.find(i => this.lostBerths.has(i)) ?? free[0];
  }
}
